import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { AppModule } from 'app/app.module';
import { environment } from '@ynet-environments/environment';
import * as Sentry from '@sentry/angular-ivy';
import { purgeDoubleEncodedOidcState } from './app/core/auth/purge-double-encoded-oidc-state';

// Run BEFORE Sentry init and Angular bootstrap so the OIDC library sees a
// clean slate if corruption is present. See purge-double-encoded-oidc-state.ts
// for the history of bugs this works around.
purgeDoubleEncodedOidcState();

// Initialize Sentry BEFORE bootstrap so it can capture errors thrown during
// module construction. Empty DSN (local dev) is a no-op.
if (environment.sentry?.dsn) {
    // Release format: em3-admin@<package.version>. The version reads from
    // package.json at build time. The Azure DevOps prod pipeline mutates
    // package.json BEFORE build to append `-build.<Build.BuildId>` (e.g.
    // 1.0.15-build.12345), so each pipeline run produces a unique release.
    // Same release-branch commit re-run produces a different BuildId =
    // different release; same artifact deployed to staging + prod shares
    // the same release with environment tags. Local builds keep the
    // unmodified package.json version so the ID stays well-formed.
    Sentry.init({
        dsn: environment.sentry.dsn,
        environment: environment.sentry.environment,
        release: `em3-admin@${environment.appVersion}`,
        tracesSampleRate: environment.sentry.tracesSampleRate,
        // Session Replay sample rates. Random session sampling is conservative
        // because the free tier is metered separately at 50 replays/mo. The
        // on-error rate is 1.0 so every error gets the ~60s of session context
        // leading up to it.
        replaysSessionSampleRate: environment.sentry.replaysSessionSampleRate,
        replaysOnErrorSampleRate: environment.sentry.replaysOnErrorSampleRate,
        integrations: [
            // Browser performance tracing (route changes, fetch/xhr timings).
            new Sentry.BrowserTracing({
                routingInstrumentation: Sentry.routingInstrumentation,
                tracePropagationTargets: [/^\//, /^https:\/\/(dev-|staging-)?api-em3\.cufi\.org/],
            }),
            // Session Replay. maskAllText + blockAllMedia are the defaults --
            // critical for em3 admin since the UI displays attendee PII and
            // payment data that must not leave the browser to Sentry.
            new Sentry.Replay({
                maskAllText: true,
                blockAllMedia: true,
            }),
        ],
        // Drop noisy errors that are user environment, not app bugs.
        ignoreErrors: [
            'ResizeObserver loop limit exceeded',
            'ResizeObserver loop completed with undelivered notifications',
            'Non-Error promise rejection captured',
        ],
    });
}

if (environment.production) {
    enableProdMode();
}

platformBrowserDynamic()
    .bootstrapModule(AppModule)
    .catch((err) => console.log(err));
